import React from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  Chip,
  Paper,
  IconButton,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WorkflowPhase, WorkflowStep } from '../types/WorkflowTypes';
import './DetailModal.css';

interface DetailModalProps {
  open: boolean;
  onClose: () => void;
  elementType: 'phase' | 'step' | null;
  elementData: WorkflowPhase | WorkflowStep | null;
}

const DetailModal: React.FC<DetailModalProps> = ({ 
  open, 
  onClose, 
  elementType, 
  elementData 
}) => {
  if (!elementData) return null;

  // Get the appropriate icon based on the step type
  const getStepIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      'document': '📄',
      'milestone': '🏁',
      'submission': '📤',
      'presentation': '🎯',
      'contract_preparation': '📝',
      'signature': '✍️',
      'offer': '💼',
      'notification': '🔔',
      'review': '👁️',
      'endorsement': '👍',
      'document_preparation': '📋',
      'application_review': '🔍',
      'project_setup': '🏗️',
      'questionnaire': '📨',
      'questionnaire_completion': '✅',
      'questionnaire_distribution': '📨',
      'contract_generation': '📜',
      'contract_signing': '✒️',
      'meeting': '👥',
      'evaluation': '⭐'
    };
    
    return iconMap[type] || '📄';
  };

  const renderPhaseDetails = (phase: WorkflowPhase) => (
    <>
      <Typography variant="h5" component="h2" gutterBottom>
        Phase: {phase.name}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {phase.description}
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle2" fontWeight="bold">
        Order: {phase.order}
      </Typography>
      
      <Typography variant="h6" sx={{ mt: 2 }}>
        Steps in this phase:
      </Typography>
      
      <List>
        {phase.steps.map((step) => (
          <ListItem key={step.id} component={Paper} elevation={1} sx={{ mb: 1, borderLeft: `4px solid ${getStepStatusColor(step.default_status)}` }}>
            <Box className="step-icon-container" sx={{ mr: 1, fontSize: '1.2rem' }}>
              {getStepIcon(step.type)}
            </Box>
            <ListItemText
              primary={step.title}
              secondary={
                <>
                  <Typography component="span" variant="body2" color="text.primary">
                    Type: {step.type}
                  </Typography>
                  <br />
                  <Typography component="span" variant="body2" color="text.secondary">
                    Status: {step.default_status}
                  </Typography>
                </>
              }
            />
          </ListItem>
        ))}
      </List>
      
      <Typography variant="h6" sx={{ mt: 3 }}>
        Customization Options:
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Blueprint customization options for this phase.
      </Typography>
      
      <Box sx={{ mt: 1 }}>
        <Chip 
          label="Add Step" 
          color="primary" 
          variant="outlined" 
          sx={{ mr: 1, mb: 1 }} 
        />
        <Chip 
          label="Reorder Steps" 
          color="primary" 
          variant="outlined" 
          sx={{ mr: 1, mb: 1 }} 
        />
        <Chip 
          label="Change Description" 
          color="primary" 
          variant="outlined" 
          sx={{ mr: 1, mb: 1 }} 
        />
      </Box>
    </>
  );

  const renderStepDetails = (step: WorkflowStep) => (
    <>
      <Box display="flex" alignItems="center" mb={1}>
        <Box className="step-icon-container" sx={{ mr: 2, fontSize: '1.8rem' }}>
          {getStepIcon(step.type)}
        </Box>
        <Typography variant="h5" component="h2">
          {step.title}
        </Typography>
      </Box>
      
      <Box display="flex" alignItems="center" mb={2}>
        <Chip 
          label={step.default_status} 
          color={getStatusColor(step.default_status)} 
          sx={{ mr: 1 }} 
        />
        <Chip 
          label={step.type} 
          variant="outlined" 
        />
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        Touchpoint: {step.touchpoint}
      </Typography>
      
      <Typography variant="subtitle2" gutterBottom>
        Involved Parties:
      </Typography>
      <Box sx={{ mb: 2 }}>
        {step.involved_parties.map((party) => (
          <Chip 
            key={party} 
            label={party} 
            size="small" 
            sx={{ mr: 0.5, mb: 0.5 }} 
          />
        ))}
      </Box>
      
      {step.dependencies && step.dependencies.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Dependencies:
          </Typography>
          <List dense>
            {step.dependencies.map((dep) => (
              <ListItem key={dep.step_id}>
                <ListItemText
                  primary={dep.step_id}
                  secondary={`Condition: ${dep.condition}`}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {step.customizable_fields && step.customizable_fields.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Customizable Fields:
          </Typography>
          <List>
            {step.customizable_fields.map((field) => (
              <ListItem key={field.field} component={Paper} elevation={1} sx={{ mb: 1 }}>
                <ListItemText
                  primary={field.field}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary">
                        Type: {field.type} {field.unit ? `(${field.unit})` : ''}
                      </Typography>
                      <br />
                      <Typography component="span" variant="body2" color="text.secondary">
                        {field.required ? 'Required' : 'Optional'}
                        {field.default !== undefined && ` • Default: ${field.default}`}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      <Typography variant="h6" sx={{ mt: 3 }}>
        Blueprint Options:
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Possible status values that can be chosen in the blueprint:
      </Typography>
      
      <Box sx={{ mt: 1 }}>
        {step.status_options.map((option) => (
          <Chip 
            key={option}
            label={option} 
            color={getStatusColor(option)} 
            variant={option === step.default_status ? 'filled' : 'outlined'}
            sx={{ mr: 1, mb: 1 }} 
          />
        ))}
      </Box>
    </>
  );

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'mandatory':
        return 'warning';
      case 'optional':
        return 'success';
      case 'disabled':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getStepStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'mandatory':
        return '#ffc107';
      case 'optional':
        return '#8bc34a';
      case 'disabled':
        return '#9e9e9e';
      default:
        return '#2196f3';
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="detail-modal-title"
      aria-describedby="detail-modal-description"
      closeAfterTransition
    >
      <Fade in={open} timeout={300}>
        <Box className="detail-modal-container">
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {elementType === 'phase' && renderPhaseDetails(elementData as WorkflowPhase)}
          {elementType === 'step' && renderStepDetails(elementData as WorkflowStep)}
          
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default DetailModal; 